import { useEffect, useRef, type RefObject } from 'react';

/**
 * The behaviour that makes a floating surface modal: focus goes in, stays in, and comes back.
 *
 * WHY A HOOK AND NOT A COMPONENT. This is the second surface in the codebase to need it and the
 * shapes have nothing in common — `PostingPanel` is a scrim with a docked drawer inside it, the
 * overlay rail is a nav that is already rendered and merely changes how it is positioned. A
 * component would have to impose a DOM shape on both, and the rail's cannot change: its brand logo
 * is a five-minute signed URL that must stay mounted across the open/close it would otherwise be
 * remounted by. So the primitive is behaviour, and the caller keeps its own markup.
 *
 * PROMOTED FROM `apps/tenders/src/PostingPanel.tsx`, which is where all of this was first written
 * and where each piece was earned:
 *
 *   - Focus RESTORE, because without it closing the panel drops focus to the top of the document
 *     and a keyboard reader loses their place entirely.
 *   - Focus INTO, because without it the surface is announced and the reader's next Tab lands back
 *     behind it.
 *   - A Tab WRAP rather than a block: blocking strands focus on whichever end the reader reached.
 *   - A class on the root rather than `inert`, which is unavailable at the Safari 14 floor. Without
 *     some freeze, a wheel over the scrim scrolls the page underneath and the surface looks modal
 *     while behaving as though it is not.
 *
 * ONE DIFFERENCE FROM THE ORIGINAL, and it is the reason this is not a copy. PostingPanel mounts
 * and unmounts, so "open" and "mounted" were the same event. A rail is always mounted and toggles,
 * so every effect here keys off `active` rather than mount — including focus capture and restore,
 * which have to happen at the transition, not at the boundaries of the component's life.
 */

/** Everything focusable, in DOM order. Queried per keystroke — a layer's content changes as it loads. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Set on <html> while any layer is open. `inert` is unavailable at the browser floor. */
export const LAYER_OPEN_CLASS = 'huaca-layer-open';

/**
 * How many layers are currently asking for the freeze.
 *
 * A bare add/remove breaks the moment there are two: closing the upper layer removes the class
 * while the lower one is still open, still trapping focus and still scrimmed — and the page behind
 * both starts scrolling again. Module-level rather than per-hook because the class is a property of
 * the DOCUMENT, and every consumer of this hook shares one.
 */
let freezeCount = 0;

export interface DismissibleLayerOptions {
  /**
   * The surface that owns focus. Give it `tabIndex={-1}` so it can hold focus itself while its
   * content is still loading — otherwise Tab escapes into the page behind on an empty layer.
   */
  ref: RefObject<HTMLElement | null>;
  /** Escape, or whatever else the caller wires to it. */
  onDismiss: () => void;
  /** False leaves the layer completely inert: no trap, no Escape, no focus movement. */
  active: boolean;
  /**
   * Freeze what is behind it. Adds `LAYER_OPEN_CLASS` to <html>; the caller's stylesheet decides
   * what that means, because what needs freezing differs per surface (a document scroller here, two
   * independent pane scrollers there).
   */
  freezeBackground?: boolean;
}

export function useDismissibleLayer({ ref, onDismiss, active, freezeBackground = true }: DismissibleLayerOptions): void {
  /*
    `onDismiss` in a ref, not in the effect's dependency array.

    Callers build it inline, so it is a new function on every render. In the deps it would tear down
    and re-attach the keydown listener on every keystroke that causes a render — and, worse, would
    re-run the focus effect below, yanking focus back to the top of the layer while somebody was
    tabbing through it. The listener is attached once per open and always calls the latest handler.
  */
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  /**
   * Who had focus when this opened, so it can be handed back.
   *
   * Captured in a ref rather than in the effect's closure because capture and restore happen in
   * different effect runs — and it is deliberately NOT read during render, where `document` may not
   * exist yet.
   */
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!active) return;

    opener.current = document.activeElement;

    // Focus moves in. The first focusable if there is one, else the surface itself — which is why
    // it needs a tabIndex. Losing this leaves the reader outside a surface that is announced as
    // modal, and their next Tab lands behind it.
    const surface = ref.current;
    const first = surface?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? surface)?.focus();

    return () => {
      // Only if it is still in the document: the element that opened the layer may itself have been
      // removed by whatever the layer did, and `focus()` on a detached node silently focuses <body>,
      // which is the same "lost my place" failure this exists to prevent.
      const restore = opener.current;
      if (restore instanceof HTMLElement && document.contains(restore)) restore.focus();
      opener.current = null;
    };
  }, [active, ref]);

  useEffect(() => {
    if (!active) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const surface = ref.current;
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss.current();
        return;
      }
      if (event.key !== 'Tab' || surface === null) return;

      const focusable = [...surface.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) {
        // Nothing to cycle through yet — hold focus on the surface rather than letting Tab out.
        event.preventDefault();
        surface.focus();
        return;
      }

      /*
        EVERY Tab inside the layer is handled here, and the native one is always cancelled.

        The obvious implementation only intercepts the two ends and lets the browser move focus in
        between — which is correct only if the browser's tab order matches this list. On macOS
        Safari it does not: "Press Tab to highlight each item" is OFF by default, so anchors are
        skipped entirely. A rail is almost all anchors, so native Tab walks straight out of a layer
        whose ends were guarded, and the reclaim below only drags focus back on the NEXT keystroke —
        focus ping-ponging in and out of a dialog on alternate presses.

        Computing the next index ourselves removes the whole class: the browser's opinion about what
        is tabbable never enters into it.
      */
      const at = document.activeElement;
      const here = focusable.indexOf(at as HTMLElement);
      const step = event.shiftKey ? -1 : 1;

      let next: number;
      if (here !== -1) {
        // Inside the list: move one, wrapping at both ends. A wrap and not a block — blocking
        // strands focus on whichever end the reader reached.
        next = (here + step + focusable.length) % focusable.length;
      } else if (at === surface) {
        // On the surface itself, which is where focus starts. Forward goes to the first control;
        // backward wraps to the last rather than escaping.
        next = event.shiftKey ? focusable.length - 1 : 0;
      } else {
        // Outside the layer entirely — a click on the page behind, or the browser handing focus
        // back from its own chrome. Pull it in.
        next = event.shiftKey ? focusable.length - 1 : 0;
      }

      event.preventDefault();
      (focusable[next] as HTMLElement).focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, ref]);

  useEffect(() => {
    if (!active || !freezeBackground) return;
    freezeCount += 1;
    document.documentElement.classList.add(LAYER_OPEN_CLASS);
    return () => {
      freezeCount -= 1;
      if (freezeCount <= 0) {
        freezeCount = 0;
        document.documentElement.classList.remove(LAYER_OPEN_CLASS);
      }
    };
  }, [active, freezeBackground]);
}
